/**
 * Local-first narration takes + chapter edit map for Narrator Mode.
 */

export type TakeStatus = "candidate" | "selected" | "discarded";

export type NarrationTake = {
  id: string;
  chapterId: string;
  filePath: string;
  createdAt: string;
  startParagraphId: string;
  endParagraphId: string;
  startOffset?: number;
  endOffset?: number;
  trimInMs: number;
  trimOutMs: number | null;
  peakDb: number;
  rmsDb: number;
  durationMs: number;
  status: TakeStatus;
  label?: string;
};

export type TimelineSegment = {
  takeId: string;
  trimInMs: number;
  trimOutMs: number;
};

export type CoverageState = "none" | "partial" | "complete";

export type ChapterEditMap = {
  chapterId: string;
  bookId: string;
  takes: NarrationTake[];
  timeline: TimelineSegment[];
  roomTonePath?: string;
  targetRmsDb: number;
  coverage: Record<string, CoverageState>;
  /** Word/paragraph alignment after stitch (optional). */
  alignmentPath?: string;
  updatedAt: string;
};

export function generateTakeId(): string {
  return `take-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createEmptyEditMap(
  chapterId: string,
  bookId: string,
  targetRmsDb = -20
): ChapterEditMap {
  return {
    chapterId,
    bookId,
    takes: [],
    timeline: [],
    targetRmsDb,
    coverage: {},
    updatedAt: new Date().toISOString(),
  };
}

export function normalizeEditMap(raw: unknown, chapterId: string, bookId: string): ChapterEditMap {
  if (!raw || typeof raw !== "object") return createEmptyEditMap(chapterId, bookId);
  const rec = raw as Record<string, unknown>;
  const takes: NarrationTake[] = [];
  if (Array.isArray(rec.takes)) {
    for (const item of rec.takes) {
      const take = normalizeTake(item, chapterId);
      if (take) takes.push(take);
    }
  }
  const timeline: TimelineSegment[] = [];
  if (Array.isArray(rec.timeline)) {
    for (const item of rec.timeline) {
      if (!item || typeof item !== "object") continue;
      const t = item as Record<string, unknown>;
      const takeId = typeof t.takeId === "string" ? t.takeId : "";
      const trimInMs = typeof t.trimInMs === "number" ? t.trimInMs : 0;
      const trimOutMs = typeof t.trimOutMs === "number" ? t.trimOutMs : 0;
      if (takeId && trimOutMs > trimInMs) timeline.push({ takeId, trimInMs, trimOutMs });
    }
  }
  const coverage: Record<string, CoverageState> = {};
  if (rec.coverage && typeof rec.coverage === "object") {
    for (const [pid, state] of Object.entries(rec.coverage as Record<string, unknown>)) {
      if (state === "none" || state === "partial" || state === "complete") {
        coverage[pid] = state;
      }
    }
  }
  return {
    chapterId,
    bookId: typeof rec.bookId === "string" ? rec.bookId : bookId,
    takes,
    timeline,
    roomTonePath: typeof rec.roomTonePath === "string" ? rec.roomTonePath : undefined,
    targetRmsDb: typeof rec.targetRmsDb === "number" ? rec.targetRmsDb : -20,
    coverage,
    alignmentPath: typeof rec.alignmentPath === "string" ? rec.alignmentPath : undefined,
    updatedAt:
      typeof rec.updatedAt === "string" ? rec.updatedAt : new Date().toISOString(),
  };
}

function normalizeTake(raw: unknown, chapterId: string): NarrationTake | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const id = typeof rec.id === "string" ? rec.id : "";
  const filePath = typeof rec.filePath === "string" ? rec.filePath : "";
  const startParagraphId = typeof rec.startParagraphId === "string" ? rec.startParagraphId : "";
  const endParagraphId = typeof rec.endParagraphId === "string" ? rec.endParagraphId : "";
  if (!id || !filePath || !startParagraphId) return null;
  const status: TakeStatus =
    rec.status === "selected" || rec.status === "discarded" || rec.status === "candidate"
      ? rec.status
      : "candidate";
  return {
    id,
    chapterId: typeof rec.chapterId === "string" ? rec.chapterId : chapterId,
    filePath,
    createdAt: typeof rec.createdAt === "string" ? rec.createdAt : new Date().toISOString(),
    startParagraphId,
    endParagraphId: endParagraphId || startParagraphId,
    startOffset: typeof rec.startOffset === "number" ? rec.startOffset : undefined,
    endOffset: typeof rec.endOffset === "number" ? rec.endOffset : undefined,
    trimInMs: typeof rec.trimInMs === "number" ? Math.max(0, rec.trimInMs) : 0,
    trimOutMs: typeof rec.trimOutMs === "number" ? rec.trimOutMs : null,
    peakDb: typeof rec.peakDb === "number" ? rec.peakDb : -6,
    rmsDb: typeof rec.rmsDb === "number" ? rec.rmsDb : -20,
    durationMs: typeof rec.durationMs === "number" ? rec.durationMs : 0,
    status,
    label: typeof rec.label === "string" ? rec.label : undefined,
  };
}

/** Recompute coverage from selected timeline takes against ordered paragraph ids. */
export function recomputeCoverage(
  editMap: ChapterEditMap,
  paragraphIds: string[]
): Record<string, CoverageState> {
  const covered = new Set<string>();
  const takeById = new Map(editMap.takes.map((t) => [t.id, t]));
  for (const seg of editMap.timeline) {
    const take = takeById.get(seg.takeId);
    if (!take || take.status === "discarded") continue;
    const startIdx = paragraphIds.indexOf(take.startParagraphId);
    const endIdx = paragraphIds.indexOf(take.endParagraphId);
    if (startIdx < 0) continue;
    const to = endIdx >= startIdx ? endIdx : startIdx;
    for (let i = startIdx; i <= to; i++) {
      const pid = paragraphIds[i];
      if (pid) covered.add(pid);
    }
  }
  const coverage: Record<string, CoverageState> = {};
  for (const pid of paragraphIds) {
    coverage[pid] = covered.has(pid) ? "complete" : "none";
  }
  return coverage;
}

export function coveragePercent(coverage: Record<string, CoverageState>): number {
  const ids = Object.keys(coverage);
  if (ids.length === 0) return 0;
  const done = ids.filter((id) => coverage[id] === "complete" || coverage[id] === "partial").length;
  return Math.round((done / ids.length) * 100);
}

export function activeTakes(editMap: ChapterEditMap): NarrationTake[] {
  return editMap.takes.filter((t) => t.status !== "discarded");
}

export function selectTakeInTimeline(
  editMap: ChapterEditMap,
  takeId: string
): ChapterEditMap {
  const take = editMap.takes.find((t) => t.id === takeId);
  if (!take) return editMap;
  const trimOut = take.trimOutMs ?? take.durationMs;
  const takes = editMap.takes.map((t) => {
    if (t.id === takeId) return { ...t, status: "selected" as const };
    // Demote other takes that cover the same start paragraph
    if (
      t.status === "selected" &&
      t.startParagraphId === take.startParagraphId &&
      t.id !== takeId
    ) {
      return { ...t, status: "candidate" as const };
    }
    return t;
  });
  // Replace timeline segment for this span or append
  const filtered = editMap.timeline.filter((seg) => {
    const other = takes.find((t) => t.id === seg.takeId);
    return !(other && other.startParagraphId === take.startParagraphId);
  });
  const timeline = [
    ...filtered,
    { takeId, trimInMs: take.trimInMs, trimOutMs: trimOut },
  ];
  return {
    ...editMap,
    takes,
    timeline,
    updatedAt: new Date().toISOString(),
  };
}
